import { SubscriptionStatus, UserLicenseType } from './couch-user';

/**
 * Subscription channel rules — what a membership may do, and where.
 *
 * Every export here answers one shape of question: given the provider a
 * membership is billed on, the platform the user is standing on right
 * now, and the membership's status, what is allowed? Whether the Cancel
 * button appears, whether Resume does, whether a purchase may be started
 * here at all, whether this member's payment is failing, and which
 * "managed somewhere else" explanation to show when the answer is no.
 *
 * Most of these are about an ordinary personal membership and have
 * nothing to do with coaching. The three that are —
 * `coachSetupConflictsWithMembership`, `describeCoachCrossChannelBlock`
 * and the type the second returns — live here rather than with the coach
 * code because they are the same rule: coaching must bill on the same
 * channel as the coach's own membership, which is the reason the rest of
 * this module exists.
 *
 * These are pure decisions, deliberately free of display text. The shared
 * package has no access to the `TranslatedString` brand, so the
 * `describe*` functions pick the CASE and the caller maps each case to
 * its own translated words.
 *
 * Split out of `coach-billing.ts` — that file is the dormant
 * `log_coach_billing` event schema and shares no symbol with any of this.
 */

/**
 * The real billing providers a personal membership or a coach plan can
 * run on. 'payPro' and 'paddle' are the two web rails; 'googlePlay' /
 * 'apple' are the two app stores. The strings match the `UserLicenseType`
 * values for these providers and the client `SubscriptionProvider` union,
 * so callers can pass either without translation. (Paddle coaching itself
 * is a later phase — see design-docs/paddle-integration.md — but a Paddle
 * *personal* membership exists in Phase 1, so the membership-management
 * gate below must accept it.)
 */
export type SubscriptionBillingProvider = 'payPro' | 'googlePlay' | 'apple' | 'paddle';

/**
 * Coaching must bill on the same channel as the coach's personal
 * membership. Returns true when starting a coach plan on `coachChannel`
 * would CONFLICT with an existing personal membership on
 * `personalProvider` — i.e. it would stack a second personal-Premium
 * grant and a second bill instead of folding in (same store) or
 * coexisting (web).
 *
 * `coachChannel` is the provider the coach plan would use: 'googlePlay'
 * on Android, 'apple' on iOS, 'payPro' on the web.
 *
 * Why this rule exists: both coach models grant the coach personal
 * Premium (the `coachData` and `coachTierData` candidates in
 * `license-reconciler`), and the per-provider blocks are parallel — so a
 * user can end up paying for a personal membership AND a coach plan at
 * the same time. A store coach tier folds in only a SAME-store
 * membership (the purchase replaces it); web metered coaching coexists
 * only with a web membership. The single non-conflicting case is
 * therefore "same provider." A user with no personal membership
 * (`personalProvider === null`) never conflicts.
 *
 * See design-docs/coach-slot-pricing.md ("same-channel rule").
 */
export function coachSetupConflictsWithMembership(
  personalProvider: SubscriptionBillingProvider | null,
  coachChannel: SubscriptionBillingProvider,
): boolean {
  if (personalProvider === null) return false;
  return personalProvider !== coachChannel;
}

/**
 * Which explanation to show when `coachSetupConflictsWithMembership` says a
 * user cannot set coaching up here. This picks the CASE, never the words —
 * the caller maps each `kind` to its own translated string. Same split as
 * `describeOffPlatformNote` below, and for the same reason: the shared
 * package has no access to the `TranslatedString` brand, so display text
 * cannot be built here.
 *
 * The four cases:
 *  - `app` — the user is inside a store app. The copy there must be plain
 *    text with no link, because the stores forbid steering to outside
 *    payment.
 *  - `store` — on our website, with a membership bought in one of the two
 *    app stores (a rare cross-login). We can name the store and steer.
 *  - `legacyWeb` — on our website, with a membership still billed by our
 *    previous web payment provider (PayPro). Coaching bills on our current
 *    web provider, so the membership has to move first. This is the one
 *    case the user cannot resolve alone, so its copy points at support.
 *  - `generic` — anything else. Kept as an explicit case rather than a
 *    default so a future provider cannot silently inherit the wrong words.
 */
export type CoachCrossChannelBlockInfo =
  | { kind: 'app' }
  | { kind: 'store'; storeName: 'App Store' | 'Google Play' }
  | { kind: 'legacyWeb' }
  | { kind: 'generic' };

/** Pick which of the four cases above applies. Call this only after
 *  `coachSetupConflictsWithMembership` has already said there IS a conflict —
 *  this helper chooses the explanation, it does not decide whether to block. */
export function describeCoachCrossChannelBlock(
  personalProvider: SubscriptionBillingProvider | null,
  isNativeApp: boolean,
): CoachCrossChannelBlockInfo {
  // if inside a store app, the store's anti-steering rules decide the copy
  // before anything about the provider matters
  if (isNativeApp) return { kind: 'app' };
  // if the membership was bought in an app store, name that store
  if (personalProvider === 'apple') return { kind: 'store', storeName: 'App Store' };
  if (personalProvider === 'googlePlay') return { kind: 'store', storeName: 'Google Play' };
  // if the membership is still on our previous web provider, it has to move
  // before coaching can start — and moving it is our job, not theirs
  if (personalProvider === 'payPro') return { kind: 'legacyWeb' };
  return { kind: 'generic' };
}

/**
 * Whether a personal membership on `provider` can be managed (or
 * purchased) from `platform`. A store membership can only be managed in
 * its own store app — Google Play on Android, Apple on iOS — and the web
 * rails (PayPro and Paddle) only on the web. This is the single rule
 * behind both the profile page's note-only "managed elsewhere" gate
 * (`subscribedOffPlatform`) and the Subscribe page's cross-platform
 * notice (the latter via `shouldBlockCrossPlatformPurchase`, which wraps
 * this rule in an active-membership gate); `platform` is Capacitor's
 * platform string.
 *
 * Note: this is intentionally STRICTER than the server's
 * `canManageSubscription`, which keeps PayPro manageable on every platform
 * so it can still drive the renews-vs-expires status display. The UI uses
 * this helper to decide which affordances to show, and leaves the server
 * flag alone. See design-docs/coach-slot-pricing.md ("same-channel rule").
 */
export function isMembershipManageableOnPlatform(
  provider: SubscriptionBillingProvider,
  platform: 'android' | 'ios' | 'web',
): boolean {
  if (provider === 'googlePlay') return platform === 'android';
  if (provider === 'apple') return platform === 'ios';
  return platform === 'web'; // payPro / paddle — the two web rails
}

/**
 * Whether `provider` is a web-billed (non-store) provider. Today that is
 * PayPro and Paddle (the two web rails), but this is written as "not one of
 * the app stores" so any future non-store web payment provider we add is
 * web-billed automatically.
 *
 * A web-billed subscription is billed through our OWN server / a web payment
 * provider, not an app store. When its cancel / resume flow is wired (PayPro
 * runs `Subscriptions/Suspend` / `Subscriptions/Renew` through our server;
 * Paddle runs `POST /subscriptions/{id}/cancel|pause|resume` through the
 * `paddleManageSubscription` callable), those actions are reachable from ANY
 * platform — there is no store-app restriction. Both web rails (PayPro and
 * Paddle) now have their in-app management flows wired (issue #4189), so the
 * in-app helpers below treat every web-billed provider as manageable.
 */
export function isWebBilledProvider(provider: SubscriptionBillingProvider): boolean {
  return provider !== 'apple' && provider !== 'googlePlay';
}

/**
 * The two shapes of the User Profile "managed elsewhere" note. `web` is the
 * generic, brand-free "managed on the web" message shown for any web-billed
 * rail (PayPro, Paddle). `store` names the specific app store for a store
 * subscription.
 */
export type OffPlatformNoteInfo =
  | { kind: 'web' }
  | { kind: 'store'; storeName: 'Google Play' | 'Apple' };

/**
 * Pick which "managed elsewhere" note to show from the membership provider.
 * Returns null when there is no provider (the note does not apply).
 *
 * Before #4277 the profile template chose with a two-way test — PayPro got the
 * web message and EVERY other provider got "managed via Apple" unless it was
 * Google Play. That mislabelled a Paddle (web) subscriber as "managed via
 * Apple" (a real customer hit this in #4268). Routing on `isWebBilledProvider`
 * fixes it: both web rails (PayPro + Paddle) get the generic web message, and
 * only the two real app stores reach the named-store branch, so no provider
 * can fall through to a wrong name.
 */
export function describeOffPlatformNote(
  provider: SubscriptionBillingProvider | null,
): OffPlatformNoteInfo | null {
  if (!provider) return null;
  // if a web rail (PayPro / Paddle), show the generic web message
  if (isWebBilledProvider(provider)) return { kind: 'web' };
  // only the two real app stores remain, so name the store
  return { kind: 'store', storeName: provider === 'googlePlay' ? 'Google Play' : 'Apple' };
}

/**
 * Whether the in-app Cancel mechanism is reachable for a subscription on
 * `provider`, viewed from `platform`. A web rail whose cancel flow is wired
 * through our server is cancellable from ANY platform; store subscriptions
 * are cancellable only in their own store app (Apple on iOS, Google on
 * Android). See issue #3826: a PayPro subscriber opening the native app must
 * still be able to cancel.
 *
 * Both web rails are wired today: PayPro (`payProUnsubscribeAsync`) and Paddle
 * (the `paddleManageSubscription` callable, action `Cancel`, issue #4189). So
 * every web-billed provider is cancellable in-app from any platform. Store
 * subscriptions stay restricted to their own store app.
 */
export function isInAppCancelAvailable(
  provider: SubscriptionBillingProvider | null,
  platform: 'android' | 'ios' | 'web',
): boolean {
  if (!provider) return false;
  // both web rails (PayPro and Paddle) have the in-app cancel flow wired, and a
  // server-driven web cancel is reachable from any platform
  if (isWebBilledProvider(provider)) return true;
  return isMembershipManageableOnPlatform(provider, platform);
}

/**
 * Whether the in-app Resume (re-activate a canceled / suspended sub)
 * mechanism is reachable for `provider`, viewed from `platform`. Same
 * provider rule as cancel, EXCEPT Google Play, which has no tested in-app
 * resume path and so is excluded. Apple resume uses the native StoreKit
 * sheet, so it stays iOS-only; PayPro resume runs through our server
 * (`payProResubscribeAsync`) and Paddle resume runs through the
 * `paddleManageSubscription` callable (action `Resume`, issue #4189) — both
 * web rails are reachable from any platform.
 */
export function isInAppResumeAvailable(
  provider: SubscriptionBillingProvider | null,
  platform: 'android' | 'ios' | 'web',
): boolean {
  if (!provider || provider === 'googlePlay') return false;
  // both web rails (PayPro and Paddle) have the in-app resume flow wired, and a
  // server-driven web resume is reachable from any platform
  if (isWebBilledProvider(provider)) return true;
  return isMembershipManageableOnPlatform(provider, platform);
}

// There is deliberately no isInAppPauseAvailable (#4189): the app offers no
// pause control on any platform, so there is nothing to gate. Reasoning:
// design-docs/paddle-integration.md, "Pause: handled, never offered".

/**
 * Whether the Subscribe page should show the "managed elsewhere"
 * cross-platform notice (and hide the buy options) instead of letting the
 * user purchase here. We only steer to the other channel while the
 * off-platform membership is STILL ACTIVE (`membershipActive`). Once it has
 * expired the user is effectively free and must be able to subscribe via
 * this platform's native store — e.g. a former PayPro web member whose sub
 * lapsed months ago, upgrading via the App Store / Play Store on the native
 * app. (Their `licenseType` stays `payPro` forever because the dead block is
 * never deleted, so provider alone can't tell "expired" from "active" —
 * `membershipActive` is the signal that distinguishes them.) See #3826.
 */
export function shouldBlockCrossPlatformPurchase(
  provider: SubscriptionBillingProvider | null,
  platform: 'android' | 'ios' | 'web',
  membershipActive: boolean,
  /** True when the membership is PAUSED — live and resumable, but currently
   *  granting no access (#4189). A paused subscriber's role drops to Unknown, so
   *  `membershipActive` is false for them; without this they would look like a
   *  free user and could buy a SECOND subscription on another provider while
   *  their Paddle one is still resumable — the concurrent multi-provider state
   *  the single-provider policy forbids, and a double bill once they resume. */
  membershipPaused: boolean,
): boolean {
  if ((!membershipActive && !membershipPaused) || !provider) return false;
  return !isMembershipManageableOnPlatform(provider, platform);
}

/**
 * Map the reconciled `licenseType` to the billing provider behind a
 * *personal* membership, or null for license types that are not a
 * personal store/web subscription (key, coach grants, none). One mapping
 * shared by the two surfaces that derive a provider from `licenseType`, so
 * they can't drift: the coach same-channel gate
 * (`coachSetupConflictsWithMembership`) and the Subscribe page's
 * cross-platform-purchase check (`shouldBlockCrossPlatformPurchase`). The
 * profile page's manage / cancel / resume gates derive their provider from
 * the live SubscriptionManager instead, not from this mapping.
 * See design-docs/coach-slot-pricing.md ("same-channel rule").
 *
 * Coach-tier reverse mapping (V6, store PRD): a coach-tier store
 * subscription grants the coach personal Premium, so when the reconciled
 * `licenseType` is `CoachTier` the "personal membership provider" is the
 * store the coach bought in — carried in `coachTierData.provider`, which
 * the caller passes as `coachTierProvider`. This is what makes
 * `shouldBlockCrossPlatformPurchase` treat an active coach as "already
 * subscribed" and block a cross-provider personal purchase, closing the
 * reverse side of the become-a-coach gate. When the caller cannot supply
 * the provider (no `coachTierData` in hand) it returns null, same as any
 * non-subscription license type. See
 * `design-docs/coach-subscription-coordination.md` §6.
 *
 * Coach-seat reverse mapping (Paddle Phase 4, #3919): a Paddle coaching
 * subscription grants the coach personal Premium via seat #1, so when the
 * reconciled `licenseType` is `CoachSeat` the "personal membership provider"
 * is always Paddle — the coach-seat rail is Paddle-only, so unlike CoachTier
 * there is no store to carry and no provider argument is needed. This is what
 * makes `shouldBlockCrossPlatformPurchase` treat an active Paddle coach as
 * "already subscribed" and block a cross-provider store personal purchase,
 * closing the reverse side of the become-a-coach gate. See
 * `design-docs/paddle-integration.md` ("Phase 4 — Coach per-seat coaching
 * subscription").
 */
export function licenseTypeToBillingProvider(
  licenseType: UserLicenseType | undefined,
  coachTierProvider?: 'apple' | 'googlePlay',
): SubscriptionBillingProvider | null {
  switch (licenseType) {
    case UserLicenseType.PayPro: return 'payPro';
    case UserLicenseType.Paddle: return 'paddle';
    case UserLicenseType.GooglePlay: return 'googlePlay';
    case UserLicenseType.Apple: return 'apple';
    // 'apple' / 'googlePlay' are themselves SubscriptionBillingProvider
    // values, so the coach-tier provider maps straight through.
    case UserLicenseType.CoachTier: return coachTierProvider ?? null;
    // the coach-seat rail is Paddle-only, so a coach-seat license always
    // maps to Paddle — no provider argument needed (unlike CoachTier).
    case UserLicenseType.CoachSeat: return 'paddle';
    default: return null;
  }
}

/**
 * Whether this membership is still live but its renewal PAYMENT is failing —
 * Paddle's `past_due` dunning window, which we store as
 * `SubscriptionStatus.Suspended`. Issue #4247.
 *
 * This is Paddle-only, and the asymmetry is the whole point of the helper. For
 * PayPro, Suspended is NOT a failing payment: it IS the canceled state, because
 * our own cancel calls PayPro's `Suspend`. So the same stored status means
 * opposite things on the two web rails, and each side must not copy the other's
 * reading. That rule used to be spelled out separately on the server and on the
 * client; it lives here now so the two cannot drift.
 *
 * `hasScheduledCancel` suppresses the state once a CANCEL is already scheduled:
 * at that point the membership is on its way out and the page shows the
 * "ends on …" note, so calling it "payment failing" as well would put two
 * contradictory messages on screen.
 *
 * A pending PAUSE deliberately does NOT suppress it. A paused-to-be membership
 * is not on its way out, the payment really is failing, and — since the webhook
 * started mirroring changes scheduled outside our app (#4247) — treating it as
 * "on its way out" left that member with no Cancel, no Resume and no note.
 */
export function isWebBilledPaymentFailing(
  provider: SubscriptionBillingProvider | null,
  status: SubscriptionStatus | null | undefined,
  hasScheduledCancel: boolean,
): boolean {
  if (provider !== 'paddle') return false;
  if (status !== SubscriptionStatus.Suspended) return false;
  return !hasScheduledCancel;
}

/**
 * Whether to tell the user that we cannot manage their web-billed membership
 * from here. True for a PayPro / Paddle membership the server reports as NOT
 * manageable — in practice a Paddle record whose stored subscription id is
 * missing, so every action flag comes back false.
 *
 * Without this the user hits a silent dead end: no Cancel button, and no
 * "managed elsewhere" note either, because that note deliberately stays quiet
 * whenever an in-app cancel exists for the provider — which it does for both
 * web rails. The panel simply rendered empty. Issue #4247 says: never a dead
 * end, so say that something is wrong and where the membership is billed.
 *
 * Written for any web-billed provider, though only Paddle can reach it today:
 * the server reports PayPro as manageable unconditionally, so `canManage` is
 * never false there. The PayPro arm is the safety net for a future rail.
 */
export function shouldShowWebMembershipProblemNote(
  provider: SubscriptionBillingProvider | null,
  canManageSubscription: boolean,
): boolean {
  if (!provider || !isWebBilledProvider(provider)) return false;
  return !canManageSubscription;
}
